import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Building2, Home, Wrench, LogOut, Crown, Bell, Megaphone } from 'lucide-react';
// I18N-2B (2026-09-23): app-shell chrome extracted into the 'common' namespace.
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';
import { surfaceNewWalkInRequests } from '../services/walkInModal';
import { useMessagesUnreadPoller, useMessagesUnread } from '../hooks/useMessagesUnread';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { EntitlementBadge } from '../components/EntitlementBadge';
import { InstallPrompt } from '../components/InstallPrompt';
import { ReviewPromptModal } from '../components/ReviewPromptModal';
import { useReviewPrompt } from '../hooks/useReviewPrompt';
import Avatar from '../components/Avatar';
import MobileMenu from '../components/MobileMenu';
import { traceSel } from '../utils/selTrace';

export type Lens = 'building' | 'home' | 'services';

// Feature flag: set true to show the Negocios lens tab to all users again.
export const SHOW_NEGOCIOS_LENS_FOR_ALL = false;

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLens, setCurrentLens] = useState<Lens>(() => {
    return (localStorage.getItem('vecindus_lens') as Lens) || 'building';
  });
  const { user, logout, roles, memberships, ownedUnits, activeCondoId, activeOwnedUnitId, setActiveCondo, setActiveOwnedUnit, unitsLoaded, contexts, activeContextId, showPersonalFlag } = useAuth();
  // I18N-2B: shell copy (context switcher, lens tabs, header buttons).
  const { t } = useTranslation('common');
  // Mensajes unread badge (2026-09-17, Jaime-approved Option 1): one poller
  // mounted here fans out via 'vecindus:messages-unread' to the desktop
  // sidebar and MobileMenu; the bell-style poll mirrors UX-2 (30s, visible-only).
  useMessagesUnreadPoller();
  const messagesUnread = useMessagesUnread();
  // Review prompt (2026-10-01): one silent eligibility check per session;
  // opens the review popup only when the backend says eligible.
  const { open: reviewPromptOpen, close: closeReviewPrompt } = useReviewPrompt();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const unitParamApplied = React.useRef(false);

  // UX-2 (2026-09-16): red unread badge on the header bell. There is no
  // lightweight count endpoint yet, so the count is derived from
  // GET /notifications/user/me and refreshed on route change and on the
  // 'vecindus:notifications-updated' window event (dispatched after
  // mark-as-read / mark-all-as-read in the notifications UI).
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let alive = true;
    let inFlight = false;
    const refresh = () => {
      // Overlap guard (2026-09-16): skip a tick if a fetch is already running.
      if (inFlight) return;
      inFlight = true;
      notificationsAPI.getAll()
        .then((res: any) => {
          if (!alive) return;
          const payload = res?.data?.data ?? res?.data;
          const items = Array.isArray(payload) ? payload : [];
          setUnreadCount(items.filter((n: any) => !n?.read).length);
          // Walk-in pop-up (2026-09-16): surface NEW unread walk_in_request
          // notifications as an app-level modal. Fires only when the poll
          // detects rows that were not in the previous snapshot; the store
          // de-dupes per session so a dismissed popup never re-pops.
          surfaceNewWalkInRequests(items);
        })
        .catch(() => { if (alive) setUnreadCount(0); })
        .finally(() => { inFlight = false; });
    };
    refresh();
    window.addEventListener('vecindus:notifications-updated', refresh);
    // Polling (2026-09-16): refresh the unread badge every 30s while the app
    // is open, only when the tab is visible. Silent badge update — no toasts
    // or sounds; the same refresh path keeps badge and panels consistent.
    const POLL_MS = 30000;
    const poll = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const timer = window.setInterval(poll, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('vecindus:notifications-updated', refresh);
    };
  }, [location.pathname]);

  const activeMembership = activeCondoId ? (roles || []).find((r) => r.condoId === activeCondoId) : undefined;
  const [fetchedName, setFetchedName] = useState<string>(() => localStorage.getItem('vecindus_condo_name') || '');
  const activeOwnedUnit = ownedUnits.find((unit) => unit.id === activeOwnedUnitId);
  // Hide membership entries whose unit is already listed as an owned unit (the unit option sets both contexts).
  // FIX 2026-09-03 (Jaime): unit-linked residents saw a near-identical
  // "Condo – unidad" MEMBERSHIP option (value condo:) next to their owned-unit
  // option (value unit:) whenever /auth/me unit identifiers didn't match the
  // owned-unit payload exactly (unitId mismatch, or unitNumber/unitLabel vs
  // unit_number/household_name formatting). Picking the membership row is an
  // explicit condo selection and bounced them to /building/dashboard. De-dupe on
  // normalized unit identity; only a genuinely different unit keeps its row.
  const normUnit = (v?: string | null) => (v || '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  // FIX 2026-09-07 (extension): /auth/me now surfaces standalone (condo-less)
  // memberships with condoId=null/condoName=null; those rendered as "- Casa Tajalí"
  // style rows under "Mis condominios" and a condo:null pick fell through to the
  // persisted active condo. Condo-less homes are already covered by the unit:
  // entries under "Mis hogares" (unlinkedOwnedUnits), so require m.condoId here.
  const visibleMemberships = (memberships || []).filter((m) => m.condoId && !ownedUnits.some((u) => {
    if (m.unitId && u.id === m.unitId) return true;
    if (!u.condo_id || m.condoId !== u.condo_id) return false;
    // Unit-less membership row for a condo that already has a linked owned unit is a redundant duplicate.
    if (!m.unitId && !m.unitNumber && !m.unitLabel) return true;
    return [m.unitNumber, m.unitLabel].some((label) => !!label && [u.unit_number, u.household_name].some((c) => normUnit(label) === normUnit(c)));
  }));
  // Condo-linked owned units belong under "Mis condominios"; "Mis hogares" is only for unlinked homes
  const linkedOwnedUnits = ownedUnits.filter((u) => u.condo_id);
  const unlinkedOwnedUnits = ownedUnits.filter((u) => !u.condo_id);
  // FIX 2026-09-22 (Jaime bug 19:21/19:41, standalone-home resident): the
  // memberships endpoint now ALSO surfaces standalone-home memberships —
  // user_condo_roles rows with condo_id NULL + unit_id set (e.g. unit 46B for
  // Isa). Those rows ARE memberships: they must count against minimalMode,
  // appear in the context switcher, and drive the unit-scoped HOME lens via
  // the existing X-Active-Unit-Id flow (same as standalone OWNED units).
  // Defensive shape handling: the backend child may ship unitId/unit_id and
  // household_name/householdName until its payload settles; accept both.
  const standaloneHomeMemberships = (memberships || []).filter((m: any) => {
    const unitId = m.unitId || m.unit_id;
    if (!unitId) return false;
    if (m.condoId || m.condo_id) return false; // condo-linked rows are handled above
    if (ownedUnits.some((u) => u.id === unitId)) return false; // already a unit: row
    return true;
  });
  const standaloneHomeLabel = (m: any) => {
    const unitId = m.unitId || m.unit_id;
    const owned = ownedUnits.find((u) => u.id === unitId);
    if (owned) return owned.household_name || owned.unit_number || t('shell.contexts.myHome');
    return m.householdName || m.household_name || m.unitLabel || m.unitNumber || m.unit_number || t('shell.contexts.myHome');
  };
  const activeStandaloneMembership = standaloneHomeMemberships.find((m: any) => (m.unitId || m.unit_id) === activeOwnedUnitId);
  // A standalone owned unit must never inherit or display a condo context.
  const hasLinkedCondo = Boolean(activeCondoId && (!activeOwnedUnit || activeOwnedUnit.condo_id === activeCondoId));
  const condoName = hasLinkedCondo ? (activeMembership?.condoName || fetchedName || t('shell.defaults.myBuilding')) : t('shell.defaults.myCondo');
  // I18N-2B: "· Mi hogar" suffix + fallback labels localize with the shell.
  const homeSuffix = ` · ${t('shell.contexts.myHome')}`;
  const homeLabel = activeOwnedUnit
    ? `${activeOwnedUnit.household_name || activeOwnedUnit.unit_number}${homeSuffix}`
    : activeStandaloneMembership
      ? `${standaloneHomeLabel(activeStandaloneMembership)}${homeSuffix}`
      : activeMembership?.unitLabel || activeMembership?.unitNumber || user?.unitLabel || user?.unit || t('shell.defaults.myHomeCap');
  const personalContext = contexts.find((c) => c.kind === 'personal');
  // 2026-09-24 (Jaime 12:05 refinement, RESIDENCY gating): hide the pure
  // 'Personal' entry only for residents/familiares bound to a unit. Owner-only
  // non-residents and empleados KEEP seeing it (server should_show_personal
  // primary; residency derivation fallback; default true -> no flash).
  // Jaime directive (2026-10-01, TH1 personal-lens lane): a user with a
  // condo-linked owned unit must NEVER be offered the pure 'Mi hogar
  // (Personal)' context in the picker — it leaks condo state and caused the
  // TH1 wipe. The personal context row stays available internally for
  // standalone-only users; unit-linked users never see or auto-activate it.
  const showPersonalContext = Boolean(personalContext) && showPersonalFlag && !ownedUnits.some((u) => !!u.condo_id);
  // FIX #92 (2026-09-23): reflect the pure personal (Hogar) context in the switcher
  // when no condo and no unit are active (previously rendered as empty selection).
  const switcherValue = activeOwnedUnitId ? `unit:${activeOwnedUnitId}` : activeCondoId ? `condo:${activeCondoId}` : (personalContext && activeContextId === personalContext.id) ? 'personal' : '';
  const handleContextChange = async (value: string) => {
    // FIX #92 (2026-09-23): pure personal (Hogar) context — bind the session JWT
    // to the personal account context and land on the home lens.
    if (value === 'personal') {
      // TH1 personal-lens wipe lane (2026-10-01): ONE activation only.
      // setActiveCondo(null) already binds the personal context (JWT + local
      // state) and clears the condo-linked active unit; the explicit pre-call
      // produced a double activation and a redundant second unit clear.
      setActiveOwnedUnit(undefined);
      await setActiveCondo(null);
      setCurrentLens('home');
      navigate('/home/dashboard');
      return;
    }
    if (value.startsWith('condo:')) {
      const condoId = value.slice(6);
      // FIX 2026-09-07: guard against a stray condo:null selection (standalone
      // condo-less membership) — never navigate to the persisted condo lens.
      if (!condoId || condoId === 'null') return;
      const membership = (roles || []).find((entry) => entry.condoId === condoId);
      // FIX 2026-09-03 (escalation): residents like jamesval_17 own NO units
      // (units.owner_user_id points at the admin), so /units/mine is empty and
      // the switcher renders ONLY condo: membership rows — those rows ARE the
      // resident's home-unit context. Picking one used to run the "explicit condo
      // selection" branch and bounced the resident to /building/dashboard. Only
      // management-capable roles (admin/directive/sub_admin/security/employee/
      // super_admin) treat a condo: pick as an explicit condo selection; a
      // resident pick lands and STAYS on the home lens.
      const residentPick = user?.role === 'resident' || membership?.role === 'resident';
      // Jaime 2026-09-30: setActiveCondo now restores the LAST-SELECTED unit
      // of the target condo (per-condo memory); do not wipe it here.
      await setActiveCondo(condoId);
      if (residentPick) {
        localStorage.setItem('vecindus_lens', 'home');
        setCurrentLens('home');
        navigate('/home/dashboard');
      } else {
        setCurrentLens('building');
        navigate('/building/dashboard');
      }
      return;
    }
    if (value.startsWith('unit:')) {
      const unitId = value.slice(5);
      // FIX 2026-09-22: the selected unit may be a standalone-home MEMBERSHIP
      // (condo_id NULL + unit_id set) rather than an owned unit. Both are
      // unit-scoped home contexts: setActiveOwnedUnit persists
      // vecindus_active_owned_unit, which the api.ts interceptor forwards as
      // X-Active-Unit-Id on every request (same flow as standalone owned units).
      // #178 (2026-09-25): the selected unit may also be a CONDO-LINKED
      // membership (resident with 2+ units in one condo — backend returns one
      // membership row per unit). Resolve the condo from that row too, then
      // persist the UNIT-level account context id to vecindus_active_context
      // so refreshes restore the picked unit (previously only the condo-level
      // context was written, making a second-unit pick a no-op).
      const unit = ownedUnits.find((entry) => entry.id === unitId);
      const standaloneMember = !unit ? standaloneHomeMemberships.find((m: any) => (m.unitId || m.unit_id) === unitId) : undefined;
      const linkedMember = !unit && !standaloneMember ? (memberships || []).find((m) => (m.unitId || (m as unknown as Record<string, unknown>).unit_id) === unitId) : undefined;
      if (!unit && !standaloneMember && !linkedMember) return;
      const memberCondoId = (linkedMember?.condoId || ((linkedMember ? (linkedMember as unknown as Record<string, unknown>).condo_id : null) as string | null)) || null;
      setActiveOwnedUnit(unitId);
      if (unit?.condo_id) {
        await setActiveCondo(unit.condo_id, true);
      } else if (memberCondoId) {
        await setActiveCondo(memberCondoId, true);
      } else {
        await setActiveCondo(null);
      }
      const unitContext = (contexts || []).find((c) => c.kind !== 'personal' && (c.unitId === unitId || (c as unknown as Record<string, unknown>).unit_id === unitId));
      if (unitContext?.id) localStorage.setItem('vecindus_active_context', unitContext.id);
      // (Jaime 2026-08-31) Unit switch via the context selector always lands on
      // the home preset. Security/employee staff keep the condo view.
      // FIX 2026-09-07: backend role is 'empleado'; the old 'employee'
      // comparison was dead and never matched.
      if (user?.role === 'security' || user?.role === 'empleado') {
        if (unit?.condo_id) {
          setCurrentLens('building');
          navigate('/building/dashboard');
        } else {
          setCurrentLens('home');
          navigate('/home/dashboard');
        }
      } else {
        setCurrentLens('home');
        navigate('/home/dashboard');
      }
    }
  };


  // Admin / super-admin get a quick entry point to the management panel.
  // FIX 2026-09-07 (Jaime parity): the role input is CONTEXT-AWARE — the
  // active condo membership role wins; global user.role is only the fallback
  // when no active context membership exists. Prevents global-role leaks
  // into the side menu (e.g. an 'empleado' membership in the active condo
  // must not surface manager items). super_admin stays a platform-level
  // override and is intentionally checked against the global role.
  const contextRole = activeMembership?.role || user?.role;
  const isManager = user?.role === 'super_admin' || contextRole === 'admin' || contextRole === 'directive';
  // Feature flag (Jaime 2026-08-31): the 'Negocios' (services) lens tab is
  // hidden from everyone except super_admin. Flip to true to re-enable it for
  // all users (one-liner). Routes stay mounted; only the switcher tab hides.
  const canSeeNegociosLens = SHOW_NEGOCIOS_LENS_FOR_ALL || user?.role === 'super_admin';

  // FIX 2026-09-16 (live-QA, Jaime report — items 4/5): a user with NO condo
  // memberships and NO owned units (e.g. a claimed invite visitor, or a
  // no-unit signup) has nothing behind the Condominio/Hogar lenses — the
  // modules rendered 400s. Those users get MINIMAL mode: a single
  // "Mi acceso" entry (their claimed pass) instead of the full lens tabs,
  // and /building|/home routes redirect to /resident-qr. Staff/platform
  // roles keep their normal shells regardless.
  // FIX 2026-09-22: standalone-home memberships (condoId NULL + unitId set)
  // count as memberships — a member resident of a standalone home must never
  // be redirected to /resident-qr as "membership-less".
  // F-2 (2026-09-23): wait for the owned-units hydration (unitsLoaded) before
  // declaring minimal mode — a standalone unit owner has NO memberships but
  // DOES own an unlinked unit; deciding before /units/mine resolves bounced
  // them to /resident-qr on every /home/* mount.
  const minimalMode = unitsLoaded && visibleMemberships.length === 0 && ownedUnits.length === 0 && standaloneHomeMemberships.length === 0
    && !['super_admin', 'security', 'empleado'].includes(String(user?.role || ''));
  const goMiAcceso = () => { setCurrentLens('home'); navigate('/home/my-qr'); };

  useEffect(() => {
    localStorage.setItem('vecindus_lens', currentLens);
  }, [currentLens]);

  // Fetch condo name when user has a condoId
  useEffect(() => {
    const condoId = activeCondoId || user?.condoId;
    if (!condoId || activeMembership?.condoName) return;
    // Fetch fresh name when the membership doesn't carry one
    fetch(`/api/condos/${condoId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const name = data?.name || data?.data?.name;
        if (name) {
          setFetchedName(name);
          localStorage.setItem('vecindus_condo_name', name);
        }
      })
      .catch(() => {});
  }, [activeCondoId, user?.condoId, activeMembership?.condoName]);

  // Lens restore guard (Jaime 2026-09-02): a standalone owned unit (no condo_id)
  // is always the personal "Hogar" context, so the shell lens must never stay
  // 'building' after switching to (or reloading with) such a unit active. This
  // covers the case where a context switch was interrupted before the
  // navigation completed and vecindus_lens was left as 'building' by
  // AuthContext.setActiveCondo.
  useEffect(() => {
    const unit = ownedUnits.find((u) => u.id === activeOwnedUnitId);
    // FIX 2026-09-22: an active standalone-home MEMBERSHIP is also a pure
    // home context — never leave the shell on the building lens for it.
    if (currentLens === 'building' && (unit && !unit.condo_id || (!unit && standaloneHomeMemberships.some((m: any) => (m.unitId || m.unit_id) === activeOwnedUnitId)))) setCurrentLens('home');
  }, [currentLens, activeOwnedUnitId, ownedUnits, memberships]);

  // #178 (2026-09-25): honor ?unitId= deep links — select that unit via the
  // normal context-switch flow, then strip the param (replace history) so a
  // refresh doesn't re-trigger the switch. Fires once, after units hydrate.
  useEffect(() => {
    if (unitParamApplied.current || !unitsLoaded) return;
    const requestedUnitId = searchParams.get('unitId');
    if (!requestedUnitId) return;
    const selectable = ownedUnits.some((u) => u.id === requestedUnitId)
      || standaloneHomeMemberships.some((m: { unitId?: string; unit_id?: string }) => (m.unitId || m.unit_id) === requestedUnitId)
      || (memberships || []).some((m) => (m.unitId || (m as unknown as Record<string, unknown>).unit_id) === requestedUnitId);
    if (!selectable) return;
    unitParamApplied.current = true;
    const next = new URLSearchParams(window.location.search);
    next.delete('unitId');
    const qs = next.toString();
    // history.replaceState instead of setSearchParams: stripping the param is
    // bookkeeping, not navigation, and avoids a setState-in-effect cascade.
    window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    // Microtask hop: the switch mutates auth state (setActiveOwnedUnit et al.),
    // and the react-hooks/set-state-in-effect rule (correctly) forbids firing
    // that synchronously inside an effect body.
    void Promise.resolve().then(() => { handleContextChange(`unit:${requestedUnitId}`).catch(() => {}); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsLoaded, searchParams, ownedUnits, memberships]);

  // Sync route with lens (if user is on /home/* but lens is building, update lens, or vice versa, based on top level)
  useEffect(() => {
    if (location.pathname.startsWith('/building') && currentLens !== 'building') setCurrentLens('building');
    if (location.pathname.startsWith('/home') && currentLens !== 'home') setCurrentLens('home');
    if (location.pathname.startsWith('/services') && currentLens !== 'services') setCurrentLens('services');
  }, [location.pathname]);

  // Minimal mode never renders condo/home module pages (they 400 without a
  // membership/unit): send the user to Mi acceso instead.
  // 2026-10-19 (v1.0.102 live finding): /home/edicion-perfil is exempt —
  // profile editing is condo-independent and renders fine with no
  // memberships/owned units, so minimalMode users keep 'Mi perfil' drawer
  // access instead of being silently bounced to /home/my-qr.
  useEffect(() => {
    if (!minimalMode) return;
    if (location.pathname === '/home/edicion-perfil') return;
    if (location.pathname.startsWith('/building') || location.pathname.startsWith('/home')) {
      navigate('/home/my-qr', { replace: true });
    }
  }, [minimalMode, location.pathname, navigate]);

  const handleLensChange = (l: Lens) => {
    setCurrentLens(l);
    navigate(`/${l}/dashboard`);
    // TH1 personal-lens wipe lane (2026-10-01): returning to the condo lens
    // after a personal dip — the personal activation drops the ACTIVE condo
    // selection (lens semantics) but must leave the condo recoverable. Restore
    // the last remembered condo via setActiveCondo, which runs the verified
    // per-condo last-unit restore (resolveLastUnitForCondo) so the unit the
    // user had selected comes back too.
    if (l === 'building' && !activeCondoId) {
      const lastCondoId = localStorage.getItem('vecindus_last_condo');
      if (lastCondoId && (roles || []).some((entry) => entry.condoId === lastCondoId)) {
        void setActiveCondo(lastCondoId).catch(() => undefined);
      }
    }
  };

  // Home is a personal lens: clear the condo context before routing.
  // FIX 2026-09-03 (Jaime bug, standalone-unit context switch): do NOT blindly
  // clear the active owned unit. If the user had a standalone (unlinked) unit
  // selected — that unit IS their "hogar" — tapping the Hogar tab must keep it
  // selected, so returning from the Condominio tab restores exactly that unit
  // and "Cambiar contexto" still shows it selected. Only condo-linked units
  // (which belong to the condo lens) are deselected, matching the 2026-08-31
  // personal-lens intent. The stored vecindus_active_owned_unit stays valid for
  // refreshMe's personalByUnit path (candidate without condo_id), so the
  // 2026-08-24/26 stale-condo purge logic is unaffected.
  const handleHomeLensChange = async () => {
    const activeUnit = ownedUnits.find((u) => u.id === activeOwnedUnitId);
    // Jaime directive (2026-10-01, TH1 personal-lens lane): for users with a
    // condo-linked owned unit, the 'Mi Condominio' <-> 'Hogar' toggle is a
    // LENS SWITCH ONLY — it must NEVER route through activatePersonalContext
    // / setActiveCondo(null), which dropped the active unit and condo
    // selection (the TH1 wipe). Active unit and condo memory stay untouched;
    // the unit-scoped home lens keeps driving X-Active-Unit-Id.
    if (ownedUnits.some((u) => !!u.condo_id)) {
      traceSel('personalAutoSwitch', { unitLinked: true, activeUnitId: activeOwnedUnitId ?? null, activeCondoId: activeCondoId ?? null, mode: 'lens-only' });
      setCurrentLens('home');
      navigate('/home/dashboard');
      return;
    }
    // FIX 2026-09-22: keep an active standalone-home MEMBERSHIP unit selected too.
    if (!(activeUnit && !activeUnit.condo_id) && !activeStandaloneMembership) setActiveOwnedUnit(undefined);
    await setActiveCondo(null);
    setCurrentLens('home');
    navigate('/home/dashboard');
  };

  // Mobile condo tab: tap navigates to the condo dashboard; long-press opens the
  // context-switch drawer (MobileMenu) instead.
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = React.useRef(false);
  const openContextMenu = () => {
    // I18N-2B: stable, language-independent selector (aria-label is translated).
    document.querySelector<HTMLButtonElement>('[aria-controls="mobile-navigation"]')?.click();
  };
  const condoTabPressStart = () => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      openContextMenu();
    }, 500);
  };
  const condoTabPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleCondoTabClick = () => {
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    handleLensChange('building');
  };

  return (
    <div className="flex h-screen v-fill-vh-fixed bg-[var(--v-base-bg)] text-[var(--v-text-primary)] overflow-hidden font-sans">
      {/* Top App Bar & Lens Switcher for Desktop */}
      <div className="app-header fixed top-0 left-0 right-0 h-16 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50 flex items-center justify-between px-4 transition-colors">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <MobileMenu messagesUnread={messagesUnread} />
          {/* MOBILE-HEADER-FIX 2026-10-17: at <640px the header overflowed and the wordmark overlapped the crown/megaphone icons. Theme toggle and logout now live below sm (both reachable from the MobileMenu drawer); logo truncates instead of overlapping. */}
          <div className="hidden sm:block md:hidden shrink-0"><ThemeToggle /></div>
          <div className="min-w-0"><Logo withText={true} /></div>
        </div>

        {/* Desktop Lens Switcher */}
        <div className="hidden md:flex items-center gap-2">
          {(visibleMemberships.length > 0 || ownedUnits.length > 0 || standaloneHomeMemberships.length > 0) ? (
            <select aria-label={t('shell.contexts.change')} value={switcherValue} onChange={(event) => handleContextChange(event.target.value).catch(() => {})} className="max-w-[260px] rounded-full border border-[var(--v-border-light)] bg-[var(--v-surface-2)] px-3 py-2 text-sm text-[var(--v-text-primary)]">
              <option value="" disabled>{t('shell.contexts.select')}</option>
              {(visibleMemberships.length > 0 || linkedOwnedUnits.length > 0) && <optgroup label={t('shell.contexts.myCondos')}>{visibleMemberships.map((membership) => { const mu = membership.unitId || ((membership as unknown as Record<string, unknown>).unit_id as string | undefined); const val = mu ? `unit:${mu}` : `condo:${membership.condoId}`; return <option key={val} value={val}>{membership.condoName} – {membership.unitLabel || membership.unitNumber || t('shell.contexts.noUnit')}</option>; })}{linkedOwnedUnits.map((unit) => <option key={`unit:${unit.id}`} value={`unit:${unit.id}`}>{unit.condo_name} – {unit.household_name || unit.unit_number}</option>)}</optgroup>}
              {(unlinkedOwnedUnits.length > 0 || standaloneHomeMemberships.length > 0 || showPersonalContext) && <optgroup label={t('shell.contexts.myHomes')}>{showPersonalContext && <option key="personal" value="personal">{t('shell.contexts.myHome')} (Personal)</option>}{unlinkedOwnedUnits.map((unit) => <option key={`unit:${unit.id}`} value={`unit:${unit.id}`}>{unit.household_name || unit.unit_number} · {t('shell.contexts.unlinked')}</option>)}{standaloneHomeMemberships.map((m: any) => { const unitId = m.unitId || m.unit_id; return <option key={`smember:${unitId}`} value={`unit:${unitId}`}>{standaloneHomeLabel(m)} · {t('shell.contexts.myHome')}</option>; })}</optgroup>}
            </select>
          ) : null}
          {/* Effective plan of the active unit context (Entitlement Sprint 2, E2) */}
          <EntitlementBadge unitId={activeOwnedUnitId} />
          {minimalMode ? (
            <div className="flex bg-[var(--v-surface-2)] p-1 rounded-full border border-[var(--v-border-light)]">
              <button
                onClick={goMiAcceso}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${location.pathname.startsWith('/home/my-qr') ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-700)] shadow-sm' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
              >
                <Home className="w-4 h-4" /> {t('shell.lens.myAccess')}
              </button>
            </div>
          ) : (
          <div className="flex bg-[var(--v-surface-2)] p-1 rounded-full border border-[var(--v-border-light)]">
          <button
            onClick={() => handleLensChange('building')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${currentLens === 'building' ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-700)] shadow-sm' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
          >
            <Building2 className="w-4 h-4" /> {condoName}
          </button>
          <button 
            onClick={() => handleLensChange('home')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${currentLens === 'home' ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-700)] shadow-sm' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
          >
            <Home className="w-4 h-4" /> {homeLabel}
          </button>
          {canSeeNegociosLens && (
          <button 
            onClick={() => handleLensChange('services')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${currentLens === 'services' ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-700)] shadow-sm' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
          >
            <Wrench className="w-4 h-4" /> {t('shell.lens.negocios')}
          </button>
          )}
          </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-3 min-w-0 shrink-0 overflow-hidden">
          {/* PWA install UX (2026-09-16): header install button (Android/Chrome) + one-time iOS guide. Hidden when already standalone. */}
          <InstallPrompt />
          <ReviewPromptModal open={reviewPromptOpen} onClose={closeReviewPrompt} />
          {isManager && (
            <button
              onClick={() => navigate('/admin')}
              title={t('shell.header.adminPanel')}
              aria-label={t('shell.header.adminPanel')}
              className="p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-teal-50,#e6f7f5)] hover:text-[var(--v-teal-700)] rounded-full transition-colors"
            >
              <Crown className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => navigate('/building/announcements')}
            title={t('shell.header.announcements')}
            aria-label={t('shell.header.viewAnnouncements')}
            data-testid="announcements-button"
            className="p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] hover:text-[var(--v-text-primary)] rounded-full transition-colors"
          >
            <Megaphone className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate('/home/notifications')}
            title={t('shell.header.notifications')}
            aria-label={t('shell.header.viewNotifications')}
            data-testid="notifications-button"
            className="relative p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] hover:text-[var(--v-text-primary)] rounded-full transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span
                data-testid="notifications-unread-badge"
                aria-label={t('shell.header.unreadCount', { count: unreadCount })}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <div className="hidden md:block"><ThemeToggle /></div>
          <button
            onClick={() => navigate('/home/edicion-perfil')}
            title={t('shell.header.myProfile')}
            aria-label={t('shell.header.myProfile')}
            className="p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] hover:text-[var(--v-text-primary)] rounded-full transition-colors"
          >
            {/* FEATURE (2026-09-01, Jaime ruling): shared <Avatar> — photo, deterministic-color initials fallback. */}
            <Avatar name={user?.name} avatarUrl={user?.avatarUrl} size={26} alt={t('shell.header.myProfile')} />
          </button>
          <button onClick={logout} title={t('shell.header.logout')} aria-label={t('shell.header.logout')} data-testid="logout-button" className="hidden sm:block p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-danger-bg)] hover:text-[var(--v-danger)] rounded-full transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Layout Area — min-w-0 and overflow-hidden prevent flex-1 children from expanding beyond viewport */}
      <div className="app-shell flex flex-1 pt-16 min-w-0 overflow-hidden">
        {/* We rely on children to render the sidebar and Dashboard content specific to the lens */}
        <div className="app-shell-bottom flex-1 w-full h-full min-h-0 overflow-hidden flex flex-col min-w-0 pb-16 md:pb-0">
          {children}
        </div>
      </div>
      
      {/* Mobile Bottom Nav (Lens Switcher) */}
      <div className="app-nav md:hidden fixed bottom-0 left-0 right-0 h-16 bg-[var(--v-surface-1)] border-t border-[var(--v-border-light)] z-50 flex justify-around items-center pb-safe box-content">
        <button 
          onClick={minimalMode ? goMiAcceso : handleCondoTabClick}
          onPointerDown={condoTabPressStart}
          onPointerUp={condoTabPressEnd}
          onPointerLeave={condoTabPressEnd}
          onPointerCancel={condoTabPressEnd}
          onContextMenu={(e) => { e.preventDefault(); openContextMenu(); }}
          title={t('shell.mobile.condoTabTitle')}
          className={`flex flex-col items-center justify-center p-2 w-full h-full transition-colors ${currentLens === 'building' ? 'text-[var(--v-teal-700)]' : 'text-[var(--v-text-secondary)]'}`}
        >
          <Building2 className="w-6 h-6 mb-1" />
          <span className="text-[10px] font-semibold truncate max-w-[80px]">{condoName}</span>
        </button>
        <button 
          onClick={() => (minimalMode ? goMiAcceso() : handleHomeLensChange().catch(() => undefined))}
          className={`flex flex-col items-center justify-center p-2 w-full h-full transition-colors ${currentLens === 'home' ? 'text-[var(--v-teal-700)]' : 'text-[var(--v-text-secondary)]'}`}
        >
          <Home className="w-6 h-6 mb-1" />
          <span className="text-[10px] font-semibold">{t('shell.lens.hogar')}</span>
        </button>
        {canSeeNegociosLens && (
        <button 
          onClick={() => handleLensChange('services')}
          className={`flex flex-col items-center justify-center p-2 w-full h-full transition-colors ${currentLens === 'services' ? 'text-[var(--v-teal-700)]' : 'text-[var(--v-text-secondary)]'}`}
        >
          <Wrench className="w-6 h-6 mb-1" />
          <span className="text-[10px] font-semibold">{t('shell.lens.negocios')}</span>
        </button>
        )}
      </div>
    </div>
  );
};
